import { useId } from 'react';
import { errorMessage, type CompanyAnnualIndicator, type CompanyReportIndicators } from './api';

interface CompanyHistoryProps {
  indicators?: CompanyReportIndicators | null;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
}

function variationLabel(value: number | null) {
  if (value == null) return '—';
  return `${value > 0 ? '+' : ''}${new Intl.NumberFormat('it-IT').format(value)}%`;
}

function solidityLabel(value: CompanyAnnualIndicator['solidita']) {
  if (value === 'verde') return 'Solida';
  if (value === 'giallo') return 'Da verificare';
  if (value === 'rosso') return 'Fragile';
  return 'n/d';
}

function VariationChart({ rows, id }: { rows: CompanyAnnualIndicator[]; id: string }) {
  const width = 360;
  const baseline = 74;
  const maxHeight = 42;
  const step = width / rows.length;
  const barWidth = Math.min(38, step * .48);
  const maxVariation = Math.max(...rows.map((row) => Math.abs(row.variazionePct ?? 0)), 1);

  return <figure className="company-history-chart">
    <figcaption>Variazione annuale del fatturato</figcaption>
    <svg viewBox={`0 0 ${width} 148`} role="img" aria-labelledby={`${id}-title ${id}-desc`} preserveAspectRatio="xMidYMid meet">
      <title id={`${id}-title`}>Variazione percentuale annuale</title>
      <desc id={`${id}-desc`}>Barre verdi per gli anni in crescita e rosse per gli anni in contrazione. I valori sono percentuali, non fatturati assoluti.</desc>
      <line className="company-history-axis" x1="0" x2={width} y1={baseline} y2={baseline}/>
      {rows.map((row, index) => {
        const value = row.variazionePct;
        const height = value == null ? 0 : Math.max(3, Math.abs(value) / maxVariation * maxHeight);
        const x = step * index + (step - barWidth) / 2;
        const y = value == null ? baseline - 1 : value >= 0 ? baseline - height : baseline;
        const labelY = value == null ? baseline - 8 : value >= 0 ? Math.max(12, y - 7) : Math.min(127, baseline + height + 14);
        return <g key={row.anno}>
          {value == null ? <line className="company-history-empty-bar" x1={x} x2={x + barWidth} y1={baseline} y2={baseline}/> : <rect className={value >= 0 ? 'is-positive' : 'is-negative'} x={x} y={y} width={barWidth} height={height} rx="4"/>}
          <text className={value == null ? 'company-history-value is-empty' : value >= 0 ? 'company-history-value is-positive' : 'company-history-value is-negative'} x={x + barWidth / 2} y={labelY} textAnchor="middle">{variationLabel(value)}</text>
          <text className="company-history-year" x={x + barWidth / 2} y="144" textAnchor="middle">{row.anno}</text>
        </g>;
      })}
    </svg>
  </figure>;
}

export default function CompanyHistory({ indicators, isLoading = false, error, onRetry }: CompanyHistoryProps) {
  const id = useId().replace(/:/g, '');
  if (isLoading && !indicators) return <section className="company-history company-history-loading" aria-label="Caricamento andamento societario" role="status"><div><span/><span/></div><i/><i/></section>;
  if (error && !indicators) return <section className="company-history company-history-error"><div className="notice notice-error"><p>{errorMessage(error)}</p>{onRetry && <button type="button" className="btn-secondary" onClick={onRetry}>Riprova andamento</button>}</div></section>;

  const rows = indicators?.serieAnnuale ?? [];
  if (rows.length < 2) return null;
  const averageDeclining = indicators?.crescitaMedia?.trim().startsWith('-');

  return <section className="company-history" aria-labelledby={`${id}-heading`}>
    <header><div><span>Serie annuale</span><h3 id={`${id}-heading`}>Andamento della società</h3></div><p>Fasce e variazioni elaborate, senza valori assoluti.</p></header>
    <VariationChart rows={rows} id={`${id}-chart`}/>
    <div className={`company-history-growth${averageDeclining ? ' is-negative' : ''}`}><span>Crescita media</span><strong>{indicators?.crescitaMedia || 'Non disponibile'}</strong><small>Media annualizzata sul periodo disponibile</small></div>
    <div className="company-history-table-wrap">
      <table className="company-history-table">
        <thead><tr><th>Anno</th><th>Dimensione</th><th>Organico</th><th>Produttività</th><th>Costo lavoro</th><th>Solidità</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.anno}>
          <th scope="row" data-label="Anno">{row.anno}<small>{variationLabel(row.variazionePct)}</small></th>
          <td data-label="Dimensione">{row.dimensione || 'n/d'}</td>
          <td data-label="Organico">{row.organico || 'n/d'}</td>
          <td data-label="Produttività">{row.produttivita || 'n/d'}</td>
          <td data-label="Costo lavoro">{row.incidenzaPersonale || 'n/d'}</td>
          <td data-label="Solidità"><span className={`company-history-solidity${row.solidita ? ` is-${row.solidita}` : ''}`}><i/>{solidityLabel(row.solidita)}</span></td>
        </tr>)}</tbody>
      </table>
    </div>
  </section>;
}
